/**
 * /pages/Roles.js – Работа со настройками должностей
 * /stores/roles.js – бизнес логика
 * 
 * Для получения списка должностей необходимо сделать запрос «Roles»
 * Для просмотра/редактирования/удаления должностей и отделов необходимо право «ManageRoles»
 * 
 * Для изменения настроек должности необходимо отправить запрос «RoleSave»
 * Чтобы удалить должность необходимо отправить запрос «RoleRemove»
 * 
 * Для получения дополнительных данных необходимо отправить запросы
 * 		«Rights» для получения списка прав
 * 		«Dashboards» для получения списка рабочих столов
 * 		«NotificationTypes» для получения списка уведомлений
 * 
 * Для работы с разделами необходимо вызвать следующие методы
 * 		«Departments» для получения списка отделов
 * 		«DepartmentSave» для получения добавления отдела
 * 		«DepartmentRemove» для удаления отдела
 */

import React, { Component } from 'react';
import { observer } from 'mobx-react';
import _ from 'lodash';
import '../styles/users.css';

import { RoleState } from '../stores/roles';
import { uistate, confirmState } from '../stores/common';
import { RoleApi, DepartmentApi, SystemApi } from '../stores/apiMethods';

import { DashboardBlock, NotificationBlock, RightsBlock } from '../shared/AccessComponents';
import { AddButton, CreateButton, ItemNewHeader } from '../shared/PageComponents';
import { TablePageContent } from '../shared/TableComponents';
import { TextInput, Selectize } from '../forms/FormItems';
import { FormModal, InlineError } from '../shared/FeedbackComponents';

import { toSelectList } from '../functions';

class RolesPage extends Component {
	constructor(props) {
		super(props);
		
		this.store = new RoleState();
	}

	componentDidMount() {
		this.getData();
		this.getDepartments();
		
		SystemApi.getRights((data) => { 
			this.store.Rights = data.Success && !_.isNil(data.Rights) ? data.Rights : [];
			if (this.store.Role) this.store.Role.Rights.Rights = this.store.Rights;
		});
		SystemApi.getDashboards((data) => { 
			this.store.Dashboards = data.Success && !_.isNil(data.Dashboards) ? data.Dashboards : [];
			if (this.store.Role) this.store.Role.Dashboards.Items = this.store.Dashboards;
		});
		
		SystemApi.getNotificationTypes((data) => { 
			this.store.Notifications = data.Success && !_.isNil(data.Notifications) ? data.Notifications : [];
			if (this.store.Role) this.store.Role.Notifications.Items = this.store.Notifications;
		});
	}

	/** API запрос «Roles» на получение списка должностей */
	getData() {
		this.store.IsSendRequest = false;
		RoleApi.getList(false, (data) => { this.store.setRoles(data); });
	}

	/**
	 * API запрос «Role» на получение должности по ID
	 * @param {number} id ID должности
	 */
	getItem(id) {
		RoleApi.getById(id, (data) => { this.store.setRole(data.Success ? data : undefined); });
	}
	/**
	 * Выбор/сброс активной должности
	 * @param {number} id ID должности или undefined (сброс)
	 */
	setId(id) {
		if (_.isUndefined(id)) {
			this.store.setRole(undefined);
			this.getData();
			return;
		}

		if (id === -1) {
			this.store.setRole({ Id : -1 });
			return;
		}

		this.getItem(id);
	}

	/**
	 * API запрос «RoleRemove» на удаление должности по ID
	 * @param {number} id ID должности
	 */
	removeId(id) { 
		if (_.isUndefined(id)) return false;

		var item = _.find(this.store.Roles, { Id : id }),
			text = <span>Вы действительно хотите удалить должность <b key="b1">«{item ? item.Name : ""}»</b>?</span>;

		confirmState.openConfirm(text, () => {
			RoleApi.remove(id, () => { this.getData(); });
		});
	}

	/** API запрос «Departments» на получение списка отделов */
	getDepartments() {
		DepartmentApi.getList((data) => { 
			this.store.Departments = data.Success && !_.isNull(data.Departments) ? data.Departments : [];
		});
	}

	render() {
		return (
			<div className="page" id="RolesPage">
				{_.isUndefined(this.store.Role) &&
					<section className="listpage">
						<div className={"head " + (uistate.IsMobile ? "mobile" : "")}>
							<h2>Управление должностями</h2>
							{this.store.CanManage &&
								<CreateButton Title="Создать должность" onClick={() => this.setId(-1)} />
							}
						</div>
						{this.store.IsSendRequest &&
							<TablePageContent
								Type={this.props.Type}
								TableData={this.store.Roles}
								TableColumns={this.store.columns}
								setId={this.setId.bind(this)}
								removeId={this.removeId.bind(this)}
							/>
						}
					</section>
				}
					{!_.isUndefined(this.store.Role) &&
						<RoleItem
							Role={this.store.Role}
							CanManage={this.store.CanManage}
							Departments={this.store.Departments}
							onClose={() => this.setId()}
							onCreateDepartment={() => this.store.setDepartment({ Id : -1 })}
							getDepartments={this.getDepartments.bind(this)}
						/>
				}
				{!_.isUndefined(this.store.Department) &&
					<DepartmentBlock store={this.store} getDepartments={this.getDepartments.bind(this)} />
				}
			</div>
		)
	}
}
RolesPage = observer(RolesPage);

class RoleItem extends Component {
	/** Закрытие окна редактирования */
	handleClose (event) { 
		// event.preventDefault(); 
		
	}

	/** API запрос «RoleSave» на сохранение данных должности */
	handleSubmit (event) {
		event.preventDefault();

		if (!this.props.Role.validate()) return false;

		this.props.Role.ButtonLoading = true;

		RoleApi.save(this.props.Role.getData(), (data) => {
			if (data.Success && this.props.Role.Id === -1) this.props.Role.Id = data.Id;

			this.props.Role.ErrorCode = data.Success ? "SUCCESS_SAVE" : data.ErrorCode;
			this.props.Role.SuccessSave = data.Success;
			this.props.Role.ButtonLoading = false;
		});
	}

	render() {
		return(
			<section className="item" id="RoleItem">
				<form onSubmit={this.handleSubmit.bind(this)} >
					<ItemNewHeader
						Title={this.props.Role.PageTitle}
						ErrorCode={this.props.Role.ErrorCode}
						ButtonLoading={this.props.Role.ButtonLoading}
						CanManage={this.props.CanManage}
						SuccessSave={this.props.Role.SuccessSave}
						handleClose={() => this.props.onClose()}
					/>
					<div className="roleGrid">
						<RoleMainInfo
							Role={this.props.Role}
							Departments={this.props.Departments}
							onCreateDepartment={this.props.onCreateDepartment}
							getDepartments={this.props.getDepartments}
						/>
						<NotificationBlock Notifications={this.props.Role.Notifications} />
						<DashboardBlock Dashboards={this.props.Role.Dashboards} />
						<RightsBlock Rights={this.props.Role.Rights} />
					</div>
				</form>
			</section>
		)
	}
}
RoleItem = observer(RoleItem);

class RoleMainInfo extends Component {
	/** API запрос на удаление отдела */
	removeDepartment = (id) => {
		if (_.isUndefined(id)) return false;

		const item = _.find(this.props.Departments, { Id : id });
		const text = <span>Вы действительно хотите удалить отдел <b key="b1">«{item ? item.Name : ""}»</b>?</span>;

		confirmState.openConfirm(text, () => {
			DepartmentApi.remove(id, (data) => {
				this.props.Role.ErrorCode = data.ErrorCode;
				this.props.getDepartments();
			});
		});
	}

	render() {
		return(
			<div className="mainInfo section">
				<TextInput
					Id="Name"
					Title="Должность"
					Required={true}
					Value={this.props.Role.Name}
					Disabled={this.props.Role.IsDefault}
					onChange={(value,id) => this.props.Role.collect(id, value)}
				/>
				<Selectize
					Id="DepartmentId"
					Title="Отдел"
					Type="Departments"
					List={toSelectList(this.props.Departments)}
					Value={this.props.Role.DepartmentId}
					Disabled={this.props.Role.IsDefault}
					Required={true}
					onRemove={this.removeDepartment}
					onChange={(value,id) => this.props.Role.collect(id, value)}
				/>
				<AddButton Title="Создать отдел" Icon={false}
					onClick={this.props.onCreateDepartment} />
			</div>
		)
	}
}
RoleMainInfo = observer(RoleMainInfo);

class DepartmentBlock extends Component {
	/** API «DepartmentSave» запрос на сохранение данных отдела */
	handleSave = () => {
		this.props.store.Department.ButtonLoading = true;

		DepartmentApi.save(this.props.store.Department.getData(), (data) => {
			if (data.Success) {
				this.props.store.setDepartment();
				this.props.getDepartments();
				this.props.store.Role.DepartmentId = data.Id;
			} else {
				this.props.store.Department.ErrorCode = data.ErrorCode;
				this.props.store.Department.ButtonLoading = false;
			}
		});
	}

	/** Закрытие окна создания отдела */
	handleClose = () => {
		this.props.store.setDepartment();
	}

	render() {
		const department = this.props.store.Department;

		return(
			<FormModal
				ClassName="department"
				Title="Добавление нового отдела"
				Loading={department.ButtonLoading}
				SubmitTitle="Сохранить"
				CancelTitle="Отменить"
				onSubmit={this.handleSave}
				onClose={this.handleClose}
			>
				<TextInput
					Id="Name"
					Title="Название отдела"
					Required={true}
					Value={department.Name}
					onChange={(value,id) => department.collect(id, value)}
				/>
				<InlineError ErrorCode={department.ErrorCode} />
			</FormModal>
		)
	}
}
DepartmentBlock = observer(DepartmentBlock);

export default RolesPage;
